/**
 * What was said at a plan gate stays on the page (spec 2026-11-02, "The plan
 * a person approves is the only contract", §5).
 *
 * An answered question no longer folds into "answered": it stays listed as a
 * line, with the answer, who gave it (Reeve's choice for a Skip), and one
 * line saying where it stands in the plan:
 *
 *   queued, applying    Reeve is updating the plan for your answer
 *   applied             Plan v4: removed step 4, 6 fewer files
 *   failed              why, and Retry (the same answer, queued again)
 *   needs_start_over    why, and Start over with this (the Start over
 *                       dialog, with the answer as its reason)
 *
 * An answer in either of the last two can also be kept as a decision: its
 * decision line goes into the plan with no step rewritten and no model call,
 * so a slow or failing model never blocks Approve.
 *
 * Change reopens the question's card; Undo withdraws the answer, and the plan
 * goes back to what it was without it. A Send back note and a reply on the
 * tracker ticket read the same way (`PlanNoteLines`), with Drop it in place
 * of Undo when one could not be applied.
 */
import type { PlanInputView } from "@yagni/shared";
import { Btn, cx } from "../queue/index.js";
import type { PlanQuestionRow } from "../work/planQuestions.js";
import {
  PLAN_ANSWER_BY_PERSON,
  PLAN_ANSWER_CHANGE_VERB,
  PLAN_ANSWER_UNDO_VERB,
  PLAN_INPUT_DISMISS_VERB,
  PLAN_UNDO_DISMISS_VERB,
  PLAN_INPUT_FAILED_FALLBACK,
  PLAN_INPUT_KEEP_AS_DECISION_HINT,
  PLAN_INPUT_KEEP_AS_DECISION_VERB,
  PLAN_INPUT_NEEDS_START_OVER_FALLBACK,
  PLAN_INPUT_RETRY_VERB,
  PLAN_INPUT_START_OVER_VERB,
  PLAN_NOTE_BY_PERSON,
  PLAN_REPLY_ON_TICKET,
  PLAN_UNDO_LABEL,
  planAnswerByReeve,
  planInputAppliedLine,
  planInputApplyingLine,
  type PlanInputNoun,
} from "../work/workCopy.js";

/** An input the sheet sent and the server has not confirmed yet. */
export function isPendingInput(input: PlanInputView): boolean {
  return input.id.startsWith("pending-");
}

function nounOf(input: PlanInputView): PlanInputNoun {
  if (input.kind === "note") return "note";
  if (input.kind === "tracker_reply") return "reply";
  if (input.kind === "undo") return "undo";
  return "answer";
}

/** Who said a line `PlanNoteLines` draws, by its kind. */
const NOTE_LINE_LABEL: Partial<Record<PlanInputView["kind"], string>> = {
  note: PLAN_NOTE_BY_PERSON,
  tracker_reply: PLAN_REPLY_ON_TICKET,
  undo: PLAN_UNDO_LABEL,
};

/** What Start over carries as its reason: the question, when there is one, and the words. */
export function startOverReasonOf(input: PlanInputView): string {
  const question = input.question?.trim();
  return question ? `${question}\n${input.body.trim()}` : input.body.trim();
}

/** An answer (a person's or Reeve's), as opposed to a note, a reply or an Undo. */
function isAnswer(input: PlanInputView): boolean {
  return input.kind === "answer" || input.kind === "reeve_answer";
}

/** Where one input stands in the plan, and the next step when it is not in it. */
export function PlanInputStatus({
  input,
  askerName,
  onRetry,
  onStartOver,
  onKeepAsDecision,
}: {
  input: PlanInputView;
  askerName: string;
  onRetry: (inputId: string) => void;
  onStartOver: (input: PlanInputView) => void;
  /** Keep an answer that did not apply as its decision; absent offers no such move. */
  onKeepAsDecision?: (inputId: string) => void;
}) {
  const noun = nounOf(input);
  if (input.status === "queued" || input.status === "applying") {
    return (
      <span className="ist busy" data-testid="plan-input-status" data-status={input.status}>
        {planInputApplyingLine(askerName, noun)}
      </span>
    );
  }
  if (input.status === "applied") {
    return (
      <span className="ist ok" data-testid="plan-input-status" data-status="applied">
        {planInputAppliedLine(input.planVersionNumber, input.changeSummary)}
      </span>
    );
  }
  if (input.status === "failed" || input.status === "needs_start_over") {
    const failed = input.status === "failed";
    const keepable = Boolean(onKeepAsDecision) && isAnswer(input) && !isPendingInput(input);
    return (
      <span className="ist err" data-testid="plan-input-status" data-status={input.status}>
        <span role="alert">
          {input.error?.trim() ||
            (failed ? PLAN_INPUT_FAILED_FALLBACK : PLAN_INPUT_NEEDS_START_OVER_FALLBACK)}
        </span>
        <span className="iacts">
          {failed && (
            <Btn variant="secondary" size="sm" onClick={() => onRetry(input.id)}>
              {PLAN_INPUT_RETRY_VERB}
            </Btn>
          )}
          <Btn
            variant={failed ? "quiet" : "secondary"}
            size="sm"
            onClick={() => onStartOver(input)}
          >
            {PLAN_INPUT_START_OVER_VERB}
          </Btn>
          {keepable && (
            <Btn
              variant="quiet"
              size="sm"
              data-testid="plan-input-keep-as-decision"
              onClick={() => onKeepAsDecision?.(input.id)}
            >
              {PLAN_INPUT_KEEP_AS_DECISION_VERB}
            </Btn>
          )}
        </span>
        {keepable && (
          <span className="ihint" data-testid="plan-input-keep-hint">
            {PLAN_INPUT_KEEP_AS_DECISION_HINT}
          </span>
        )}
      </span>
    );
  }
  return null;
}

export interface PlanAnswerLinesProps {
  /** The questions answered lines are drawn for, in the plan's order. */
  questions: readonly PlanQuestionRow[];
  /** The live answers on the gate (`isPlanAnswer`), one per question at most. */
  answers: readonly PlanInputView[];
  askerName: string;
  /** Change: the question's card opens again. */
  onChange: (questionId: string) => void;
  onUndo: (questionId: string) => void;
  onRetry: (inputId: string) => void;
  onStartOver: (input: PlanInputView) => void;
  /** Keep an answer that did not apply as its decision line. */
  onKeepAsDecision?: (inputId: string) => void;
}

export function PlanAnswerLines({
  questions,
  answers,
  askerName,
  onChange,
  onUndo,
  onRetry,
  onStartOver,
  onKeepAsDecision,
}: PlanAnswerLinesProps) {
  const rows = questions.flatMap((question) => {
    const answer = answers.find((entry) => entry.questionId === question.id);
    return answer ? [{ question, answer }] : [];
  });
  if (rows.length === 0) return null;
  return (
    <div className="decided planswers" data-testid="plan-answer-lines">
      {rows.map(({ question, answer }) => {
        const reeves = answer.skipped || answer.kind === "reeve_answer";
        const sending = isPendingInput(answer);
        return (
          <div key={question.id} data-testid="plan-answer-line" data-status={answer.status}>
            <div className={cx("dl", !reeves && "mine")}>
              <span className="dq">
                <span className="qq">{question.text}</span>
                <span className="aa">
                  <span className="by">
                    {reeves ? planAnswerByReeve(askerName) : PLAN_ANSWER_BY_PERSON}
                  </span>
                  {answer.body}
                </span>
                <PlanInputStatus
                  input={answer}
                  askerName={askerName}
                  onRetry={onRetry}
                  onStartOver={onStartOver}
                  onKeepAsDecision={onKeepAsDecision}
                />
              </span>
              <Btn
                variant="quiet"
                size="sm"
                disabled={sending}
                onClick={() => onChange(question.id)}
              >
                {PLAN_ANSWER_CHANGE_VERB}
              </Btn>
              <Btn variant="quiet" size="sm" disabled={sending} onClick={() => onUndo(question.id)}>
                {PLAN_ANSWER_UNDO_VERB}
              </Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The Send back notes and the replies on the ticket said at the gate, and an
 * Undo still on its way into the plan (or one that could not be). Applied
 * Undos are not drawn: the question they reopened says it.
 */
export function PlanNoteLines({
  inputs,
  askerName,
  onRetry,
  onDismiss,
  onStartOver,
}: {
  inputs: readonly PlanInputView[];
  askerName: string;
  onRetry: (inputId: string) => void;
  onDismiss: (inputId: string) => void;
  onStartOver: (input: PlanInputView) => void;
}) {
  const rows = inputs.filter(
    (input) =>
      input.kind === "note" ||
      input.kind === "tracker_reply" ||
      (input.kind === "undo" && input.status !== "applied"),
  );
  if (rows.length === 0) return null;
  return (
    <div className="decided planswers" data-testid="plan-note-lines">
      {rows.map((input) => {
        // What did not apply can be let go: a note or a reply is dropped, and
        // an Undo dropped leaves the plan with the answer it still holds.
        const dismissable =
          !isPendingInput(input) &&
          (input.status === "failed" || input.status === "needs_start_over");
        return (
          <div key={input.id} data-testid="plan-note-line" data-status={input.status}>
            <div className={cx("dl", input.kind === "note" && "mine")}>
              <span className="dq">
                {input.kind === "undo" && input.question ? (
                  <span className="qq">{input.question}</span>
                ) : null}
                <span className="aa">
                  <span className="by">{NOTE_LINE_LABEL[input.kind]}</span>
                  <span className="nb">{input.body}</span>
                </span>
                <PlanInputStatus
                  input={input}
                  askerName={askerName}
                  onRetry={onRetry}
                  onStartOver={onStartOver}
                />
              </span>
              {dismissable && (
                <Btn variant="quiet" size="sm" onClick={() => onDismiss(input.id)}>
                  {input.kind === "undo" ? PLAN_UNDO_DISMISS_VERB : PLAN_INPUT_DISMISS_VERB}
                </Btn>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
